import { useCallback, useEffect, useRef, useState } from "react";
import { draftKey, differsFrom, plainValues, purgeExpired, readDraft, removeDraft, writeDraft } from "../lib/drafts";

const SAVE_DELAY_MS = 800;

// Keeps what a person typed in a long form on this device (Ordre 24-4). `values`: the plain fields of the form; `enabled`: false while the
// form is loading or edits an existing record (the server already holds that one). Nothing is applied by itself: a found draft is offered
// (`pending`), `restore()` hands its values to `apply`, `dismiss()` forgets it, `clear()` is called once the server accepted the form.
export default function useDraft({ name, userId, values, apply, enabled = true }) {
  const key = draftKey(userId, name);
  const baseline = useRef(null);
  const [pending, setPending] = useState(null);
  const [decided, setDecided] = useState(false);
  const stopped = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    purgeExpired(userId);
    const found = readDraft(key);
    if (found) setPending(found);
    else setDecided(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  useEffect(() => {
    if (!enabled || !decided || stopped.current) return undefined;
    if (baseline.current === null) {
      baseline.current = plainValues(values);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (stopped.current) return;
      if (differsFrom(baseline.current, values)) writeDraft(key, plainValues(values));
      else removeDraft(key);
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled, decided, key, values]);

  const restore = useCallback(() => {
    if (!pending) return;
    apply(pending.values);
    setPending(null);
    setDecided(true);
  }, [pending, apply]);

  const dismiss = useCallback(() => {
    removeDraft(key);
    setPending(null);
    setDecided(true);
  }, [key]);

  const clear = useCallback(() => {
    stopped.current = true;
    removeDraft(key);
  }, [key]);

  return { pending, restore, dismiss, clear };
}
